import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import ChevronDownIcon from "@react-spectrum/s2/icons/ChevronDown";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  View,
  Flex,
  Text,
  Button,
  TextField,
  Checkbox,
  Picker,
  PickerItem,
  MenuTrigger,
  Menu,
  MenuItem,
  asS2Icon,
} from "./Common/spectrumCompat";
import { displayToasts } from "../Helpers/displayAppToast";
import DxpRegistrationTopHeader from "./DxpRegistrationTopHeader";
import WizardNavLayout from "./WizardNavLayout";
import {
  setRegisteredCompanies,
  setSelectedRegisterCompanyType,
  setPartnerRegistrationFormStep,
  setDxpUserProfileDraft,
} from "../Redux/actions";
import {
  getDXPRegistrationLink,
  unflattenWithSeparator,
  getCommonAPIHeaders,
  getStoredMembershipType,
  persistPartnerLevel,
} from "../Helpers/utils";
import { saveRegistrationForm } from "../Services/PartnerRegistrationAPI";
import { PROGRAM_SPECIFIC_NAMES } from "../Helpers/constants";
import FullScreenLoader from "./Common/FullScreenLoader";
import useUnsavedChangesWarning from "../Hooks/useUnsavedChangesWarning";
import userProfileConfig from "../ContentConfig/UserProfileStep";
import { DxpPhoneNumberField } from "./Common";
import { getDxpOnboardingTermsPdfUrl } from "../Helpers/dxpOnboardingTerms";
import {
  DXP_FIELD_MAX_LENGTH,
  formatDialPrefixOnly,
  formatPhoneDialDisplay,
  buildDxpSecondaryRegistrationPayload,
  validateDxpPersonalProfileFields,
} from "../Helpers/dxpRegistrationValidation";

const ChevronDown = asS2Icon(ChevronDownIcon);

/* eslint-disable @typescript-eslint/no-explicit-any */
/** `id` is required for React Spectrum `items` + collection (same as countrycode). */
interface CountryItem {
  id: string;
  countrycode: string;
  country: string;
  defaultTimeZone?: string;
  phoneCode?: string;
}

type FacetPickerItem = { id: string; label: string };

const secondaryFooterPillLabelClass = "dxp-membership-secondary-footer-pill";

const primaryFooterPillLabelClass = "dxp-membership-primary-footer-pill";

/** Ensures string keys, dedupes, and skips invalid rows — invalid keys break React Spectrum Picker. */
function normalizeCountryRows(raw: unknown): CountryItem[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: CountryItem[] = [];
  raw.forEach((row) => {
    if (!row || typeof row !== "object") return;
    const r = row as Record<string, unknown>;
    if (r.countrycode == null || r.countrycode === "") return;
    const code = String(r.countrycode).trim();
    if (!code || seen.has(code)) return;
    seen.add(code);
    const name =
      typeof r.country === "string" && r.country.trim()
        ? r.country.trim()
        : code;
    out.push({
      id: code,
      countrycode: code,
      country: name,
      defaultTimeZone:
        typeof r.defaultTimeZone === "string" ? r.defaultTimeZone : undefined,
      phoneCode: typeof r.phoneCode === "string" ? r.phoneCode : undefined,
    });
  });
  return out;
}

interface FacetItem {
  jobLevel: string[];
  jobFunction: string[];
  primaryRoleWithAdobe: string[];
  countryCode?: Array<{ name: string; value: string }>;
  /** Company step (CompanyInformation); stored with shared facet cache. */
  ownership?: string[];
  /** Country name -> list of time zones; used to filter the Time zone picker by the selected Country/Region. */
  timeZoneCountryMappings?: Record<string, string[]>;
}

/** Shared `userProfileFacets` cache may only contain `ownership` from CompanyInformation. */
function normalizeUserProfileFacets(raw: unknown): FacetItem {
  const data =
    raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const rawMappings = data.timeZoneCountryMappings;
  let timeZoneCountryMappings: Record<string, string[]> | undefined;
  if (rawMappings && typeof rawMappings === "object") {
    timeZoneCountryMappings = {};
    Object.entries(rawMappings as Record<string, unknown>).forEach(
      ([country, zones]) => {
        if (Array.isArray(zones)) {
          timeZoneCountryMappings![country] = zones.filter(
            (z): z is string => typeof z === "string",
          );
        }
      },
    );
  }
  return {
    jobLevel: Array.isArray(data.jobLevel) ? data.jobLevel : [],
    jobFunction: Array.isArray(data.jobFunction) ? data.jobFunction : [],
    primaryRoleWithAdobe: Array.isArray(data.primaryRoleWithAdobe)
      ? data.primaryRoleWithAdobe
      : [],
    countryCode: Array.isArray(data.countryCode) ? data.countryCode : undefined,
    ownership: Array.isArray(data.ownership) ? data.ownership : undefined,
    timeZoneCountryMappings,
  };
}

function hasUserProfileFacetLists(facets: FacetItem): boolean {
  return (
    facets.jobLevel.length > 0 ||
    facets.jobFunction.length > 0 ||
    facets.primaryRoleWithAdobe.length > 0
  );
}
/* eslint-enable @typescript-eslint/no-explicit-any */

const PROGRAM = "DXP";

/** `personalInfo_*` keys from the shared flat blob (same shape as step 3 Next persists). */
function getPersonalInfoFromSessionStorage(): Record<string, unknown> | null {
  try {
    const regStr = sessionStorage.getItem("registrationFormData");
    if (!regStr) return null;
    const flat = JSON.parse(regStr) as Record<string, unknown>;
    const personalFlat: Record<string, unknown> = {};
    Object.keys(flat).forEach((k) => {
      if (k.startsWith("personalInfo_")) personalFlat[k] = flat[k];
    });
    if (Object.keys(personalFlat).length === 0) return null;
    const tree = unflattenWithSeparator(personalFlat, "_") as {
      personalInfo?: Record<string, unknown>;
    };
    const pi = tree?.personalInfo;
    if (pi && typeof pi === "object" && !Array.isArray(pi)) {
      return pi as Record<string, unknown>;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function normalizePersonalInfoPhoneLine(
  pi: Record<string, unknown>,
): Record<string, unknown> {
  const out = { ...pi };
  const line = out.phoneWithoutCountryCode;
  const hasLine =
    (typeof line === "string" && line.trim()) ||
    (typeof line === "number" && String(line).trim());
  if (hasLine) return out;

  const combinedRaw = out.phone;
  let combined = "";
  if (typeof combinedRaw === "string") {
    combined = combinedRaw.trim();
  } else if (typeof combinedRaw === "number") {
    combined = String(combinedRaw).trim();
  }
  const idx = combined.indexOf("-");
  if (idx !== -1) {
    out.phoneWithoutCountryCode = combined.slice(idx + 1).trim();
  }
  return out;
}

/**
 * Restore step 3 after returning from company (or resume). Merge Redux draft with
 * `registrationFormData` so a partial `draft.personalInfo` (e.g. API resume) does not
 * hide phones and other fields still present only in session.
 */
function getPersonalInfoForHydration(
  draft: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null {
  const draftNested = draft?.personalInfo;
  let draftPi: Record<string, unknown> | null = null;
  if (
    draftNested &&
    typeof draftNested === "object" &&
    !Array.isArray(draftNested)
  ) {
    draftPi = {
      ...(draftNested as Record<string, unknown>),
    };
  }
  const sessionPi = getPersonalInfoFromSessionStorage();

  if (!draftPi && !sessionPi) return null;

  const merged = normalizePersonalInfoPhoneLine({
    ...(draftPi || {}),
    ...(sessionPi || {}),
  });
  return merged;
}

function getUserProfileStepTotal(
  selectedType: string | null | undefined,
  joiningExistingCompany = false,
): number {
  if (joiningExistingCompany) return 3;
  if (selectedType === "subsidaryCompany") return 4;
  if (selectedType === "standaloneCompany") return 5;
  return 5;
}

function getUserProfilePrimaryButtonLabel(
  joiningExistingCompany: boolean,
  nextButtonLabel: string,
  registerButtonLabel: string,
): string {
  if (!joiningExistingCompany) return nextButtonLabel;
  return registerButtonLabel;
}

function getPrimaryRoleMenuLabel(
  selected: string[],
  items: FacetPickerItem[],
): string {
  if (selected.length === 0) return "Select";
  const labels = selected.map(
    (id) => items.find((i) => i.id === id)?.label ?? id,
  );
  return labels.join(", ");
}

type UserProfileProps = {
  /** Dev preview: do not sync Redux step; left nav uses fixed step/company type. */
  previewMode?: boolean;
};

type AppState = {
  registration?: {
    partnerRegistrationFormStep?: number;
    selectedRegisterCompanyType?: string | null;
    registeredCompanies?: unknown[];
    dxpUserProfileDraft?: Record<string, unknown> | null;
  };
};

function UserProfile({ previewMode = false }: UserProfileProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const registration = useSelector(
    (state: AppState) => state?.registration || {},
  );

  const [countries, setCountries] = useState<CountryItem[]>([]);
  const [facets, setFacets] = useState<FacetItem>({
    jobLevel: [],
    jobFunction: [],
    primaryRoleWithAdobe: [],
  });

  const [countryCode, setCountryCode] = useState<string>("");
  const [phoneDialCountryCode, setPhoneDialCountryCode] = useState<string>("");
  const [phoneWithoutCountryCode, setPhoneWithoutCountryCode] = useState("");
  const [timeZone, setTimeZone] = useState<string>("");
  const [previouslyRegistered, setPreviouslyRegistered] = useState(false);
  const [previouslyRegisteredEmail, setPreviouslyRegisteredEmail] =
    useState("");
  const [jobLevel, setJobLevel] = useState<string>("");
  const [jobFunction, setJobFunction] = useState<string>("");
  const [customJobFunction, setCustomJobFunction] = useState("");
  const [primaryRoleWithAdobe, setPrimaryRoleWithAdobe] = useState<string[]>(
    [],
  );
  const [agreeToTos, setAgreeToTos] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  /** After user edits phone fields, re-validate phone on each change (below-field errors). */
  const [phoneFieldTouched, setPhoneFieldTouched] = useState(false);
  /** Live validation for previously registered email only after the user edits the field (not on first show). */
  const [previouslyRegisteredEmailDirty, setPreviouslyRegisteredEmailDirty] =
    useState(false);

  const profileHydratedRef = useRef(false);

  const allowNavigation = useUnsavedChangesWarning(
    true,
    "Changes done in form may not be saved. Are you sure you want to leave?",
  );

  const apiUrl =
    window?.partnerRegistrationConfig?.REACT_APP_API_URL ||
    "https://partner-registration-dev.adobe.io";

  const fetchCountries = useCallback(async () => {
    const cached = sessionStorage.getItem("userProfileCountries");
    if (cached) {
      try {
        setCountries(normalizeCountryRows(JSON.parse(cached)));
        return;
      } catch {
        sessionStorage.removeItem("userProfileCountries");
      }
    }
    try {
      const headers = getCommonAPIHeaders();
      const res = await fetch(`${apiUrl}/api/v1/DXP/country`, { headers });
      if (res.ok) {
        const data = await res.json();
        const normalized = normalizeCountryRows(data);

        setCountries(normalized);
        sessionStorage.setItem(
          "userProfileCountries",
          JSON.stringify(normalized),
        );
      }
    } catch (err) {
      // eslint-disable-next-line no-console -- network diagnostics
      console.error("Failed to fetch countries", err);
    }
  }, [apiUrl]);

  const fetchFacets = useCallback(async () => {
    const cached = sessionStorage.getItem("userProfileFacets");
    if (cached) {
      try {
        const normalized = normalizeUserProfileFacets(JSON.parse(cached));
        if (hasUserProfileFacetLists(normalized)) {
          setFacets(normalized);
          return;
        }
        /* ownership-only cache from CompanyInformation — load full facets from API. */
      } catch {
        sessionStorage.removeItem("userProfileFacets");
      }
    }
    try {
      const headers = getCommonAPIHeaders();
      const res = await fetch(`${apiUrl}/api/v1/DXP/partner/facet`, {
        headers,
      });
      if (res.ok) {
        const data = await res.json();
        let parsed: Record<string, unknown> = {};
        try {
          const existing = sessionStorage.getItem("userProfileFacets");
          if (existing) parsed = JSON.parse(existing);
        } catch {
          parsed = {};
        }
        const f = normalizeUserProfileFacets({ ...parsed, ...data });
        setFacets(f);
        sessionStorage.setItem("userProfileFacets", JSON.stringify(f));
      }
    } catch (err) {
      // eslint-disable-next-line no-console -- network diagnostics
      console.error("Failed to fetch facets", err);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchCountries();
    fetchFacets();
  }, [fetchCountries, fetchFacets]);

  useEffect(() => {
    if (previewMode || profileHydratedRef.current) return;
    const pi = getPersonalInfoForHydration(
      registration?.dxpUserProfileDraft ?? null,
    );
    if (!pi) return;

    profileHydratedRef.current = true;

    if (typeof pi.country === "string" && pi.country.trim()) {
      setCountryCode(pi.country.trim());
    }
    if (typeof pi.phoneCountryCode === "string" && pi.phoneCountryCode.trim()) {
      setPhoneDialCountryCode(pi.phoneCountryCode.trim());
    }
    const rawLine = pi.phoneWithoutCountryCode;
    if (typeof rawLine === "string" && rawLine.trim()) {
      setPhoneWithoutCountryCode(rawLine.trim());
    } else if (typeof rawLine === "number" && String(rawLine).trim()) {
      setPhoneWithoutCountryCode(String(rawLine).trim());
    }
    if (typeof pi.timeZone === "string" && pi.timeZone.trim()) {
      setTimeZone(pi.timeZone.trim());
    }
    if (typeof pi.previouslyRegistered === "boolean") {
      setPreviouslyRegistered(pi.previouslyRegistered);
    }
    if (typeof pi.previouslyRegisteredEmail === "string") {
      setPreviouslyRegisteredEmail(pi.previouslyRegisteredEmail.trim());
    }
    if (typeof pi.jobLevel === "string" && pi.jobLevel.trim()) {
      setJobLevel(pi.jobLevel.trim());
    }
    if (typeof pi.jobFunction === "string" && pi.jobFunction.trim()) {
      setJobFunction(pi.jobFunction.trim());
    }
    if (typeof pi.customJobFunction === "string") {
      setCustomJobFunction(pi.customJobFunction.trim());
    }
    if (Array.isArray(pi.primaryRoleWithAdobe)) {
      setPrimaryRoleWithAdobe(pi.primaryRoleWithAdobe as string[]);
    }
    if (typeof pi.agreeToTos === "boolean") {
      setAgreeToTos(pi.agreeToTos);
    }
  }, [previewMode, registration?.dxpUserProfileDraft]);

  useEffect(() => {
    if (previewMode) return;
    if (registration?.partnerRegistrationFormStep !== 3) {
      dispatch(setPartnerRegistrationFormStep(3));
    }
  }, [dispatch, registration?.partnerRegistrationFormStep, previewMode]);

  const selectedCompanyTypeForStep =
    registration?.selectedRegisterCompanyType ??
    localStorage.getItem("selectedRegisterCompanyType");
  const accountId = localStorage.getItem("accountId");
  const membershipType = getStoredMembershipType();
  const isJoiningExistingCompany = Boolean(
    accountId &&
    accountId !== "undefined" &&
    !["standaloneCompany", "subsidaryCompany"].includes(
      selectedCompanyTypeForStep || "",
    ),
  );
  const stepTotal = getUserProfileStepTotal(
    selectedCompanyTypeForStep,
    isJoiningExistingCompany,
  );
  const primaryButtonLabel = getUserProfilePrimaryButtonLabel(
    isJoiningExistingCompany,
    userProfileConfig.nextButton,
    userProfileConfig.registerButton,
  );

  const handleGoBack = () => {
    if (previewMode) {
      displayToasts({
        type: "info",
        text: "Preview mode — back is disabled.",
        autoClose: true,
      });
      return;
    }
    sessionStorage.clear();
    localStorage.removeItem("RedirectPath");
    localStorage.removeItem("accountId");
    localStorage.removeItem("companyName");
    localStorage.removeItem("accountStatus");
    localStorage.removeItem("email");
    localStorage.removeItem("selectedRegisterCompanyType");
    localStorage.removeItem("emailMismatch");
    localStorage.removeItem("partnerRegistrationFormStep");
    localStorage.removeItem("redirectTo");
    dispatch(setSelectedRegisterCompanyType(null));
    dispatch(setRegisteredCompanies(registration.registeredCompanies || [], 0));
    if (typeof window !== "undefined") {
      window.location.href = getDXPRegistrationLink();
    }
  };

  const validate = (): boolean => {
    const e = validateDxpPersonalProfileFields({
      countryCode,
      phoneDialCountryCode,
      phoneWithoutCountryCode,
      timeZone,
      previouslyRegistered,
      previouslyRegisteredEmail,
      jobLevel,
      jobFunction,
      customJobFunction,
      primaryRoleWithAdobe,
      agreeToTos,
    });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  useEffect(() => {
    if (!phoneFieldTouched) return;
    setErrors((prev) => {
      const e = validateDxpPersonalProfileFields({
        countryCode,
        phoneDialCountryCode,
        phoneWithoutCountryCode,
        timeZone,
        previouslyRegistered,
        previouslyRegisteredEmail,
        jobLevel,
        jobFunction,
        customJobFunction,
        primaryRoleWithAdobe,
        agreeToTos,
      });
      const next = { ...prev };
      if (e.phone) next.phone = e.phone;
      else delete next.phone;
      return next;
    });
    // Only phone inputs should re-run this merge; do not list other fields here or
    // `previouslyRegisteredEmail` changes will re-run this effect and only merge `phone`,
    // leaving a stale `errors.previouslyRegisteredEmail`.
  }, [phoneFieldTouched, phoneDialCountryCode, phoneWithoutCountryCode]);

  useEffect(() => {
    if (!previouslyRegistered) {
      setPreviouslyRegisteredEmailDirty(false);
      setErrors((prev) => {
        if (!prev.previouslyRegisteredEmail) return prev;
        const next = { ...prev };
        delete next.previouslyRegisteredEmail;
        return next;
      });
      return;
    }
    if (!previouslyRegisteredEmailDirty) return;
    setErrors((prev) => {
      const e = validateDxpPersonalProfileFields({
        countryCode,
        phoneDialCountryCode,
        phoneWithoutCountryCode,
        timeZone,
        previouslyRegistered,
        previouslyRegisteredEmail,
        jobLevel,
        jobFunction,
        customJobFunction,
        primaryRoleWithAdobe,
        agreeToTos,
      });
      const next = { ...prev };
      if (e.previouslyRegisteredEmail) {
        next.previouslyRegisteredEmail = e.previouslyRegisteredEmail;
      } else {
        delete next.previouslyRegisteredEmail;
      }
      return next;
    });
    /* Email rule only depends on checkbox + value; keep deps minimal. */
  }, [
    previouslyRegistered,
    previouslyRegisteredEmail,
    previouslyRegisteredEmailDirty,
  ]);

  const buildPersonalInfoFlatData = useCallback((): Record<string, unknown> => {
    const dialCountry = countries.find(
      (c) => c.countrycode === phoneDialCountryCode,
    );
    const phoneCode = dialCountry?.phoneCode || phoneDialCountryCode;
    const fullPhone = phoneWithoutCountryCode.trim()
      ? `${phoneCode}-${phoneWithoutCountryCode.trim()}`
      : "";

    const data: Record<string, unknown> = {
      personalInfo_isDifferentLocation: false,
      personalInfo_country: countryCode,
      personalInfo_phoneCountryCode: phoneDialCountryCode || countryCode,
      personalInfo_phone: fullPhone,
      personalInfo_phoneWithoutCountryCode: phoneWithoutCountryCode.trim(),
      personalInfo_timeZone: timeZone,
      personalInfo_previouslyRegistered: previouslyRegistered,
      personalInfo_previouslyRegisteredEmail: previouslyRegistered
        ? previouslyRegisteredEmail.trim()
        : undefined,
      personalInfo_jobLevel: jobLevel,
      personalInfo_jobFunction: jobFunction,
      personalInfo_customJobFunction:
        jobFunction === "Other" ? customJobFunction.trim() : undefined,
      personalInfo_primaryRoleWithAdobe: primaryRoleWithAdobe,
      personalInfo_agreeToTos: agreeToTos,
    };

    Object.keys(data).forEach((k) => {
      if (data[k] === undefined) delete data[k];
    });
    return data;
  }, [
    agreeToTos,
    countries,
    countryCode,
    customJobFunction,
    jobFunction,
    jobLevel,
    phoneDialCountryCode,
    phoneWithoutCountryCode,
    previouslyRegistered,
    previouslyRegisteredEmail,
    primaryRoleWithAdobe,
    timeZone,
  ]);

  const persistPersonalInfoToSessionAndDraft = useCallback(
    (data: Record<string, unknown>) => {
      let persistedRegistration: Record<string, unknown> = {};
      try {
        const regStr = sessionStorage.getItem("registrationFormData");
        if (regStr) persistedRegistration = JSON.parse(regStr);
      } catch {
        persistedRegistration = {};
      }
      sessionStorage.setItem(
        "registrationFormData",
        JSON.stringify({ ...persistedRegistration, ...data }),
      );

      const nestedPersonal = unflattenWithSeparator(data, "_") as Record<
        string,
        unknown
      >;

      dispatch(
        setDxpUserProfileDraft({
          ...(registration.dxpUserProfileDraft || {}),
          ...nestedPersonal,
        }),
      );
    },
    [dispatch, registration.dxpUserProfileDraft],
  );

  /**
   * Step 3 Next: persist personal profile to Redux only (no /register), then advance to company info.
   * Aligns with ref CustomNextButtonRenderer flow (personal info → company step).
   */
  const handleNext = () => {
    if (isJoiningExistingCompany) return;
    if (!validate()) return;
    if (previewMode) {
      displayToasts({
        type: "info",
        text: "Preview mode — next is disabled.",
        autoClose: true,
      });
      return;
    }

    persistPersonalInfoToSessionAndDraft(buildPersonalInfoFlatData());
    allowNavigation();
    dispatch(setPartnerRegistrationFormStep(4));
  };

  const handleRegister = async () => {
    if (!validate()) return;
    if (previewMode) {
      displayToasts({
        type: "info",
        text: "Preview mode — register is disabled.",
        autoClose: true,
      });
      return;
    }
    if (!accountId) return;

    const personalFlat = buildPersonalInfoFlatData();
    persistPersonalInfoToSessionAndDraft(personalFlat);

    let sessionFormData: Record<string, unknown> | null = null;
    try {
      const persistedStr = sessionStorage.getItem("registrationFormData");
      if (persistedStr) {
        sessionFormData = JSON.parse(persistedStr) as Record<string, unknown>;
      }
    } catch {
      sessionFormData = null;
    }

    const payload = buildDxpSecondaryRegistrationPayload({
      personalFlat,
      accountId,
      companyName: localStorage.getItem("companyName"),
      membershipType,
      sessionFormData,
    });

    setIsLoading(true);
    try {
      const response: { status?: number; message?: string; data?: unknown } =
        await saveRegistrationForm(
          PROGRAM_SPECIFIC_NAMES[PROGRAM].programType,
          payload,
        );

      if (response.status !== 1) {
        displayToasts({
          type: "error",
          text: response?.message ?? "Something went wrong please try again.",
          autoClose: true,
        });
        return;
      }

      const responseData = response?.data as { errorCode?: string } | undefined;
      if (responseData?.errorCode === "10016") {
        displayToasts({
          type: "error",
          text: "Alternate email address is already associated with another contact",
          autoClose: true,
        });
        return;
      }
      if (
        ["10004", "10008", "10001", "10003"].includes(
          responseData?.errorCode || "",
        )
      ) {
        displayToasts({
          type: "error",
          text: "Your email is already registered.",
          autoClose: true,
        });
        return;
      }
      if (responseData?.errorCode === "10002") {
        displayToasts({
          type: "error",
          text: "You have registered with another program.",
          autoClose: true,
        });
        return;
      }

      const accountStatus = localStorage.getItem("accountStatus");
      const companyNameForSuccess = localStorage.getItem("companyName");
      const responseCompanyInfo = (
        response?.data as { companyInfo?: { level?: string } } | undefined
      )?.companyInfo;
      const membershipTypeForSuccess =
        membershipType ||
        (payload.companyInfo as Record<string, unknown>)?.level ||
        responseCompanyInfo?.level;

      localStorage.clear();
      if (accountStatus) localStorage.setItem("accountStatus", accountStatus);
      if (accountId) localStorage.setItem("accountId", accountId);
      if (companyNameForSuccess)
        localStorage.setItem("companyName", companyNameForSuccess);
      if (membershipTypeForSuccess) {
        persistPartnerLevel({ level: membershipTypeForSuccess });
      }
      allowNavigation();
      navigate(PROGRAM_SPECIFIC_NAMES[PROGRAM].route.requestSubmitted);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrimaryAction = async () => {
    if (isJoiningExistingCompany) {
      await handleRegister();
      return;
    }
    handleNext();
  };

  const isFormValid = useMemo(() => {
    const e = validateDxpPersonalProfileFields({
      countryCode,
      phoneDialCountryCode,
      phoneWithoutCountryCode,
      timeZone,
      previouslyRegistered,
      previouslyRegisteredEmail,
      jobLevel,
      jobFunction,
      customJobFunction,
      primaryRoleWithAdobe,
      agreeToTos,
    });
    return Object.keys(e).length === 0;
  }, [
    countryCode,
    phoneDialCountryCode,
    phoneWithoutCountryCode,
    timeZone,
    previouslyRegistered,
    previouslyRegisteredEmail,
    jobLevel,
    jobFunction,
    customJobFunction,
    primaryRoleWithAdobe,
    agreeToTos,
  ]);

  /** Match disabled styling: block Next/Register until valid (preview stays clickable for QA toasts). */
  const userProfilePrimaryActionDisabled =
    isLoading || (!previewMode && !isFormValid);

  /** Picker crashes on open if selectedKey is not present in the collection. */
  const countryPickerKey = useMemo((): string | null => {
    if (!countryCode) return null;
    return countries.some((c) => c.countrycode === countryCode)
      ? countryCode
      : null;
  }, [countryCode, countries]);

  const phoneDialPickerKey = useMemo((): string | null => {
    if (!phoneDialCountryCode) return null;
    return countries.some((c) => c.countrycode === phoneDialCountryCode)
      ? phoneDialCountryCode
      : null;
  }, [phoneDialCountryCode, countries]);

  const phoneDialPrefixDisplay = useMemo(() => {
    const c = countries.find((x) => x.countrycode === phoneDialCountryCode);
    return formatDialPrefixOnly(c?.phoneCode);
  }, [countries, phoneDialCountryCode]);

  /** Name of the selected Country/Region, used to key into `timeZoneCountryMappings`. */
  const selectedCountryName = useMemo(
    (): string =>
      countries.find((c) => c.countrycode === countryCode)?.country ?? "",
    [countries, countryCode],
  );

  /** Time zones for the selected Country/Region; empty (picker disabled) until a country with a mapping is selected. */
  const timeZoneOptionsForCountry = useMemo((): string[] => {
    const mappings = facets.timeZoneCountryMappings;
    return mappings?.[selectedCountryName]?.length
      ? mappings[selectedCountryName]
      : [];
  }, [facets.timeZoneCountryMappings, selectedCountryName]);

  const timeZonePickerKey = useMemo((): string | null => {
    if (!timeZone) return null;
    return timeZoneOptionsForCountry.includes(timeZone) ? timeZone : null;
  }, [timeZone, timeZoneOptionsForCountry]);

  const jobLevelPickerKey = useMemo((): string | null => {
    if (!jobLevel) return null;
    return (facets.jobLevel ?? []).includes(jobLevel) ? jobLevel : null;
  }, [jobLevel, facets.jobLevel]);

  const jobFunctionPickerKey = useMemo((): string | null => {
    if (!jobFunction) return null;
    return (facets.jobFunction ?? []).includes(jobFunction)
      ? jobFunction
      : null;
  }, [jobFunction, facets.jobFunction]);

  const timeZoneFacetItems = useMemo((): FacetPickerItem[] => {
    const seen = new Set<string>();
    return timeZoneOptionsForCountry
      .filter((tz) => {
        if (!tz || seen.has(tz)) return false;
        seen.add(tz);
        return true;
      })
      .map((tz) => ({ id: tz, label: tz }));
  }, [timeZoneOptionsForCountry]);

  const jobLevelFacetItems = useMemo((): FacetPickerItem[] => {
    const seen = new Set<string>();
    return (facets.jobLevel ?? [])
      .filter((j) => {
        if (!j || seen.has(j)) return false;
        seen.add(j);
        return true;
      })
      .map((j) => ({ id: j, label: j }));
  }, [facets.jobLevel]);

  const jobFunctionFacetItems = useMemo((): FacetPickerItem[] => {
    const seen = new Set<string>();
    return (facets.jobFunction ?? [])
      .filter((j) => {
        if (!j || seen.has(j)) return false;
        seen.add(j);
        return true;
      })
      .map((j) => ({ id: j, label: j }));
  }, [facets.jobFunction]);

  const primaryRoleMenuItems = useMemo((): FacetPickerItem[] => {
    const seen = new Set<string>();
    return (facets.primaryRoleWithAdobe ?? [])
      .filter((r) => {
        if (!r || seen.has(r)) return false;
        seen.add(r);
        return true;
      })
      .map((r) => ({ id: r, label: r }));
  }, [facets.primaryRoleWithAdobe]);

  const primaryRoleSelectedKeys = useMemo(
    () => new Set(primaryRoleWithAdobe),
    [primaryRoleWithAdobe],
  );

  return (
    <div className="dxp-registration-page-shell registration-page-wrapper">
      {isLoading ? <FullScreenLoader /> : null}
      <DxpRegistrationTopHeader program={PROGRAM} />
      <div className="dxp-registration-content-row registration-content-wrapper">
        <WizardNavLayout
          previewStepOverride={previewMode ? 3 : undefined}
          previewCompanyTypeOverride={
            previewMode ? "standaloneCompany" : undefined
          }
        />
        <View UNSAFE_className="dxp-wizard-main-column">
          <View UNSAFE_className="dxp-wizard-step-panel dxp-co-step-panel-stack">
            <Flex
              direction="row"
              justifyContent="space-between"
              alignItems="start"
              UNSAFE_className="dxp-wizard-step-heading-row dxp-wizard-flex-heading-row"
            >
              <Text UNSAFE_className="dxp-wizard-step-title-text">
                {userProfileConfig.title}
              </Text>
              <Text UNSAFE_className="dxp-wizard-step-meta-label">
                {`STEP 3/${stepTotal}`}
              </Text>
            </Flex>
            <div className="dxp-co-title-rule" aria-hidden />

            <h2 className="dxp-co-section-heading-title dxp-co-section-heading">
              {userProfileConfig.personalInfoTitle ?? "Personal information"}
            </h2>
            <Text UNSAFE_className="dxp-profile-block-intro">
              {userProfileConfig.personalInfoHint}
            </Text>

            <Picker
              label="Country/Region"
              placeholder="Select"
              isRequired
              necessityIndicator="icon"
              items={countries}
              selectedKey={countryPickerKey}
              onSelectionChange={(key) => {
                if (key == null) return;
                const code = String(key);
                setCountryCode(code);
                setPhoneDialCountryCode(code);
              }}
              isInvalid={!!errors.country}
              errorMessage={errors.country}
              isDisabled={countries.length === 0}
              UNSAFE_className="dxp-mb-16 dxp-w-full drop-field"
            >
              {(item: CountryItem) => (
                <PickerItem id={item.id} textValue={item.country}>
                  {item.country}
                </PickerItem>
              )}
            </Picker>

            <View UNSAFE_className="dxp-mb-16 dxp-w-full">
              <Text UNSAFE_className="dxp-profile-phone-label">
                Personal phone number
                <span className="dxp-required-marker--alt"> *</span>
              </Text>
              <DxpPhoneNumberField
                countrySelector={
                  <Picker
                    aria-label="Phone country code"
                    placeholder="Select"
                    size="M"
                    items={countries}
                    selectedKey={phoneDialPickerKey}
                    onSelectionChange={(key) => {
                      if (key == null) return;
                      setPhoneFieldTouched(true);
                      setPhoneDialCountryCode(String(key));
                    }}
                    isInvalid={!!errors.phone}
                    isDisabled={countries.length === 0}
                    UNSAFE_className="dxp-w-full drop-field"
                  >
                    {(item: CountryItem) => {
                      const dial = formatPhoneDialDisplay(
                        item.phoneCode,
                        item.countrycode,
                      );
                      return (
                        <PickerItem
                          id={item.id}
                          textValue={`${item.countrycode} ${item.country} ${dial}`}
                        >
                          {item.countrycode}
                        </PickerItem>
                      );
                    }}
                  </Picker>
                }
                dialPrefix={phoneDialPrefixDisplay || undefined}
                value={phoneWithoutCountryCode}
                onChange={(v) => {
                  setPhoneFieldTouched(true);
                  setPhoneWithoutCountryCode(v);
                }}
                placeholder="Enter phone number"
                aria-label="Personal phone number"
                isInvalid={!!errors.phone}
                maxLength={20}
                size="M"
                pickerColumnWidth={80}
                UNSAFE_className="dxp-w-full"
              />
              {errors.phone ? (
                <Text UNSAFE_className="dxp-co-error-text">{errors.phone}</Text>
              ) : null}
            </View>

            <Picker
              label="Time zone"
              placeholder="Select"
              isRequired
              necessityIndicator="icon"
              items={timeZoneFacetItems}
              selectedKey={timeZonePickerKey}
              onSelectionChange={(key) => {
                if (key == null) return;
                setTimeZone(String(key));
              }}
              isInvalid={!!errors.timeZone}
              errorMessage={errors.timeZone}
              isDisabled={timeZoneFacetItems.length === 0}
              UNSAFE_className="dxp-mb-16 dxp-w-full drop-field"
            >
              {(item: FacetPickerItem) => (
                <PickerItem id={item.id} textValue={item.label}>
                  {item.label}
                </PickerItem>
              )}
            </Picker>

            <Checkbox
              isSelected={previouslyRegistered}
              onChange={(selected) => {
                setPreviouslyRegistered(selected);
                if (!selected) {
                  setPreviouslyRegisteredEmailDirty(false);
                }
              }}
              UNSAFE_className="dxp-profile-checkbox-row"
            >
              {userProfileConfig.previouslyRegisteredLabel}
            </Checkbox>
            <Text UNSAFE_className="dxp-profile-merge-hint">
              {userProfileConfig.mergeHint}
            </Text>

            {previouslyRegistered && (
              <TextField
                label="Previously registered email"
                placeholder="Enter email"
                isRequired
                necessityIndicator="icon"
                maxLength={
                  DXP_FIELD_MAX_LENGTH.personalInfo_previouslyRegisteredEmail
                }
                value={previouslyRegisteredEmail}
                onChange={(v) => {
                  setPreviouslyRegisteredEmailDirty(true);
                  setPreviouslyRegisteredEmail(v);
                }}
                isInvalid={!!errors.previouslyRegisteredEmail}
                errorMessage={errors.previouslyRegisteredEmail}
                UNSAFE_className="dxp-profile-email-field-mt dxp-w-full"
              />
            )}

            <h2 className="dxp-co-section-heading-title dxp-co-section-heading dxp-mt-28">
              {userProfileConfig.personalProfileTitle ?? "Personal profile"}
            </h2>
            <Text UNSAFE_className="dxp-profile-block-intro">
              {userProfileConfig.personalProfileHint}
            </Text>

            <Picker
              label={userProfileConfig.jobLevelLabel}
              placeholder="Select"
              isRequired
              necessityIndicator="icon"
              items={jobLevelFacetItems}
              selectedKey={jobLevelPickerKey}
              onSelectionChange={(key) => {
                if (key == null) return;
                setJobLevel(String(key));
              }}
              isInvalid={!!errors.jobLevel}
              errorMessage={errors.jobLevel}
              isDisabled={jobLevelFacetItems.length === 0}
              UNSAFE_className="dxp-mb-16 dxp-w-full drop-field"
            >
              {(item: FacetPickerItem) => (
                <PickerItem id={item.id} textValue={item.label}>
                  {item.label}
                </PickerItem>
              )}
            </Picker>

            <Picker
              label={userProfileConfig.jobFunctionLabel}
              placeholder="Select"
              isRequired
              necessityIndicator="icon"
              items={jobFunctionFacetItems}
              selectedKey={jobFunctionPickerKey}
              onSelectionChange={(key) => {
                if (key == null) return;
                setJobFunction(String(key));
              }}
              isInvalid={!!errors.jobFunction}
              errorMessage={errors.jobFunction}
              isDisabled={jobFunctionFacetItems.length === 0}
              UNSAFE_className="dxp-mb-16 dxp-w-full drop-field"
            >
              {(item: FacetPickerItem) => (
                <PickerItem id={item.id} textValue={item.label}>
                  {item.label}
                </PickerItem>
              )}
            </Picker>

            {jobFunction === "Other" && (
              <TextField
                label="Don't see the job role that fits what you do? Describe it below."
                placeholder="Enter"
                isRequired
                necessityIndicator="icon"
                value={customJobFunction}
                onChange={setCustomJobFunction}
                maxLength={DXP_FIELD_MAX_LENGTH.personalInfo_customJobFunction}
                isInvalid={!!errors.customJobFunction}
                errorMessage={errors.customJobFunction}
                UNSAFE_className="dxp-mb-16 dxp-w-full"
              />
            )}

            <View UNSAFE_className="dxp-mb-16 dxp-w-full">
              <Text UNSAFE_className="dxp-profile-multiselect-label">
                {userProfileConfig.primaryRoleLabel}
                <span className="dxp-required-marker--alt"> *</span>
              </Text>
              <MenuTrigger>
                <Button
                  variant="secondary"
                  UNSAFE_className="dxp-profile-primary-role-trigger"
                >
                  <span
                    className={`dxp-profile-primary-role-value ${
                      primaryRoleWithAdobe.length > 0
                        ? "dxp-profile-primary-role-value--set"
                        : "dxp-profile-primary-role-value--placeholder"
                    }`}
                  >
                    {getPrimaryRoleMenuLabel(
                      primaryRoleWithAdobe,
                      primaryRoleMenuItems,
                    )}
                  </span>
                  <ChevronDown UNSAFE_className="dxp-icon-16-svg" />
                </Button>
                <Menu
                  items={primaryRoleMenuItems}
                  selectionMode="multiple"
                  selectedKeys={primaryRoleSelectedKeys}
                  onSelectionChange={(keys) => {
                    if (keys === "all") return;
                    setPrimaryRoleWithAdobe(
                      Array.from(keys as Set<React.Key>, (k) => String(k)),
                    );
                  }}
                >
                  {(item: FacetPickerItem) => (
                    <MenuItem id={item.id} textValue={item.label}>
                      {item.label}
                    </MenuItem>
                  )}
                </Menu>
              </MenuTrigger>
              {errors.primaryRoleWithAdobe ? (
                <Text UNSAFE_className="dxp-co-error-text">
                  {errors.primaryRoleWithAdobe}
                </Text>
              ) : null}
            </View>

            <div className="dxp-profile-terms-title">
              {userProfileConfig.termsTitle}
            </div>

            <div className="dxp-profile-terms-panel">
              <Text UNSAFE_className="dxp-profile-terms-paragraph">
                The{" "}
                <a
                  href="https://www.adobe.com/privacy/policy.html#info-share"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="dxp-text-accent-link"
                >
                  Adobe family of companies
                </a>{" "}
                may keep me informed with{" "}
                <a
                  href="https://www.adobe.com/privacy/marketing.html#mktg-email"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="dxp-text-accent-link"
                >
                  personalized
                </a>{" "}
                emails about the{" "}
                <a
                  href="https://partners.adobe.com/digitalexperience/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="dxp-text-accent-link"
                >
                  Adobe Digital Experience Partner Program
                </a>
                .
              </Text>
              <Text UNSAFE_className="dxp-profile-terms-paragraph dxp-profile-terms-paragraph--mb16">
                See our{" "}
                <a
                  href="https://www.adobe.com/privacy/policy.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="dxp-text-accent-link"
                >
                  Privacy Policy
                </a>{" "}
                for more details or to{" "}
                <a
                  href="https://account.adobe.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="dxp-text-accent-link"
                >
                  opt out
                </a>{" "}
                at any time.
              </Text>
              <Checkbox
                isSelected={agreeToTos}
                onChange={setAgreeToTos}
                isInvalid={!!errors.agreeToTos}
              >
                <span className="dxp-profile-checkbox-terms-label">
                  {userProfileConfig.termsAgreePrefix}
                  <a
                    href={getDxpOnboardingTermsPdfUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="dxp-text-accent-link"
                    onClick={(ev) => ev.stopPropagation()}
                    onKeyDown={(ev) => ev.stopPropagation()}
                  >
                    {userProfileConfig.termsAgreeLinkText}
                  </a>
                  {userProfileConfig.termsAgreeSuffix}
                </span>
              </Checkbox>
              {errors.agreeToTos ? (
                <Text UNSAFE_className="dxp-co-error-text">
                  {errors.agreeToTos}
                </Text>
              ) : null}
            </div>

            <Flex
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              UNSAFE_className="dxp-wizard-footer-actions dxp-wizard-footer-actions--pair dxp-wizard-footer-actions--mt-shell"
            >
              <Button
                variant="secondary"
                UNSAFE_className={secondaryFooterPillLabelClass}
                onPress={handleGoBack}
              >
                Back
              </Button>
              <Button
                variant="accent"
                isDisabled={userProfilePrimaryActionDisabled}
                onPress={handlePrimaryAction}
                UNSAFE_className={[
                  primaryFooterPillLabelClass,
                  userProfilePrimaryActionDisabled
                    ? "dxp-wizard-accent-next--disabled"
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {primaryButtonLabel}
              </Button>
            </Flex>
          </View>
        </View>
      </div>
    </div>
  );
}

UserProfile.defaultProps = {
  previewMode: false,
};

export {
  normalizeCountryRows,
  getUserProfileStepTotal,
  getUserProfilePrimaryButtonLabel,
  getPrimaryRoleMenuLabel,
};
export default UserProfile;
